import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import {
  totalDedication,
  validDedication,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/dedication'
import { FormattedAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'
import useLegalEntities from 'modules/employees/hooks/useLegalEntities'
import ValueRow, {
  ValueAssignment,
} from 'modules/finance/components/Dimensions/DimensionMultipleValuePicker/ValueRow'

import { useCostCenterByLegalEntities } from './hooks/useCostCenterByLegalEntities'

interface Props {
  assignments: FormattedAssignment[]
  updateAssignment: (valueId: string, newValue: ValueAssignment) => void
  deleteValue: (value: ValueAssignment) => void
  addNewAssignment: () => void
  onSearchChange?: (search: string) => void
  readonly?: boolean
}

const CostCenterPicker = ({
  assignments,
  updateAssignment,
  deleteValue,
  addNewAssignment,
  readonly,
}: Props) => {
  const { legalEntities } = useLegalEntities()
  const { controls, rawCostCenterOptions, debouncedOnSearch, filteredCostCenterOptions, loading } =
    useCostCenterByLegalEntities({
      excludeCostCenterIds: assignments
        .map((assignment) => assignment.costCenterId)
        .filter((id): id is number => id != null),
      legalEntityIds: legalEntities.map((legalEntity) => legalEntity.id),
    })

  const disableAddButton = rawCostCenterOptions.length === assignments.length

  useUnsafeEffect(() => {
    if (assignments.length === 0) {
      addNewAssignment()
    }
  }, [addNewAssignment, assignments])

  return (
    <Box gap="s16">
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell ellipsis wide>
              {i18n.t('finance.employee_dimensions_info.modals.set_assignments.cost_center')}
            </Table.HeadCell>
            <Table.HeadCell ellipsis wide>
              {i18n.t('finance.employee_dimensions_info.modals.set_assignments.dedication')}
            </Table.HeadCell>
            <Table.HeadCell width={40} wide />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          <>
            {assignments.map((value) => (
              <ValueRow
                key={value.costCenterId}
                value={{
                  id: String(value.costCenterId),
                  name: value.name,
                  percentage: value.percentage,
                }}
                placeholder={i18n.t('finance.cost_centers.assignments.editable_card.select')}
                options={filteredCostCenterOptions}
                controls={controls}
                loading={loading}
                onChange={updateAssignment}
                onDelete={(v) =>
                  deleteValue({
                    id: v.id,
                    name: v.name,
                    percentage: 0,
                  })
                }
                readonly={readonly}
                withUnassignedPlaceholder={false}
                onSearchChange={debouncedOnSearch}
              />
            ))}
            <Table.Row background="grey200">
              <Table.Cell wide>
                <Text.Normal size="200" weight="semibold" color="grey1000">
                  {i18n.t('finance.employee_dimensions_info.modals.set_assignments.total')}
                </Text.Normal>
              </Table.Cell>
              <Table.Cell wide>
                <Text.Normal
                  size="200"
                  weight="semibold"
                  color={validDedication(assignments) ? 'grey1000' : 'radicalRed1200'}
                >
                  {totalDedication(assignments)}
                </Text.Normal>
              </Table.Cell>
              <Table.Cell />
            </Table.Row>
          </>
        </Table.Body>
      </Table>
      {!readonly && (
        <Button
          onClick={addNewAssignment}
          type="secondary"
          disabled={disableAddButton}
          iconName="add"
        >
          {i18n.t('finance.employee_dimensions_info.modals.set_assignments.buttons.add')}
        </Button>
      )}
    </Box>
  )
}

export default CostCenterPicker
